import React, { useState, useContext, useRef, useEffect } from "react";
import {
  Box, Container, Paper, Grid, Typography, TextField, Button, Avatar, Alert, CircularProgress, Tabs, Tab, Divider,
  Card, CardContent, IconButton, useMediaQuery, Chip, Stack, Backdrop, Tooltip, useTheme as useMuiTheme, InputAdornment // Renamed to useMuiTheme
} from "@mui/material";
import { alpha } from "@mui/material/styles"; // For styling with transparency
import { AuthContext } from "../context/AuthContext";
import { authService } from "../services/api";
import { useLanguage } from "../context/LanguageContext";
import PageContainer from "../components/PageContainer";
import SignatureCanvas from "react-signature-canvas";

// MUI Icons
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined"; // Page Icon & Profile Tab
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined"; // Security Tab
import GestureOutlinedIcon from "@mui/icons-material/GestureOutlined"; // Signature Tab
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
// import CancelIcon from "@mui/icons-material/Cancel"; // Not explicitly used for actions
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined"; // For clearing signature
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"; // Can be used for edit actions
// import BadgeIcon from "@mui/icons-material/Badge"; // Not used
// import NotificationsIcon from "@mui/icons-material/Notifications"; // Not used
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import PasswordOutlinedIcon from "@mui/icons-material/PasswordOutlined";
// import VisibilityIcon from "@mui/icons-material/Visibility"; // Not used for password visibility here
import BrushOutlinedIcon from "@mui/icons-material/BrushOutlined"; // For pen settings
// import ColorLensIcon from "@mui/icons-material/ColorLens"; // Not used
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";

// Tab panel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`profile-tabpanel-${index}`} aria-labelledby={`profile-tab-${index}`} {...other}>
      {value === index && <Box sx={{ p: { xs: 2, sm: 3 } }}>{children}</Box>}
    </div>
  );
}

const Profile = () => {
  const { currentUser, updateProfile, error: authErrorHook } = useContext(AuthContext);
  const { t } = useLanguage();
  const theme = useMuiTheme(); // MUI theme
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // Changed breakpoint for tabs

  const [loading, setLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null); // Combined error state
  const [tabValue, setTabValue] = useState(0);

  const [formData, setFormData] = useState({ name: "", email: "", phoneNumber: "", department: "" });
  const [passwordData, setPasswordData] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });

  const signatureRef = useRef(null);
  const fileInputRef = useRef(null);
  const signatureContainerRef = useRef(null); // Ref for signature container for sizing

  const [signaturePreviewUrl, setSignaturePreviewUrl] = useState(null); // For displaying saved signature
  const [penColor, setPenColor] = useState(theme.palette.text.primary); // Default to theme text color
  const [penSize, setPenSize] = useState(2);


  useEffect(() => {
    if (currentUser) {
      setFormData({
        name: currentUser.name || "", email: currentUser.email || "",
        phoneNumber: currentUser.phoneNumber || "", department: currentUser.department || "",
      });
      setSignaturePreviewUrl(currentUser.signature || null);
      // Set pen color based on theme mode when component mounts or theme changes
      setPenColor(theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000');
    }
  }, [currentUser, theme.palette.mode]);

  // Resize signature canvas
  useEffect(() => {
    let resizeTimer;
    const resizeCanvas = () => {
      if (tabValue === 2 && signatureRef.current && signatureContainerRef.current) {
        const canvas = signatureRef.current.getCanvas();
        const container = signatureContainerRef.current;
        const prevData = signatureRef.current.toData(); // Save current drawing

        const rect = container.getBoundingClientRect();
        canvas.width = rect.width; // Use full container width
        canvas.height = 200; // Fixed height

        signatureRef.current.fromData(prevData); // Redraw previous data
        signatureRef.current.penColor = penColor; // Re-apply pen color
        signatureRef.current.minWidth = penSize; // Re-apply pen size (MUI uses minWidth for penWeight)
        signatureRef.current.maxWidth = penSize + 1; // Example
      }
    };
    if (tabValue === 2) { resizeTimer = setTimeout(resizeCanvas, 50); } // Small delay for container render
    window.addEventListener("resize", resizeCanvas);
    return () => { window.removeEventListener("resize", resizeCanvas); clearTimeout(resizeTimer); };
  }, [tabValue, penColor, penSize]);


  const handleTabChange = (event, newValue) => setTabValue(newValue);
  const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handlePasswordChange = (e) => setPasswordData({ ...passwordData, [e.target.name]: e.target.value });

  const displayMessage = (type, messageKey, fallbackMessage) => {
    if (type === 'success') setSuccessMessage(t(messageKey) || fallbackMessage);
    else setErrorMessage(t(messageKey) || fallbackMessage);
    setTimeout(() => { setSuccessMessage(null); setErrorMessage(null); }, 3000);
  };

  const saveSignature = async () => {
    if (signatureRef.current?.isEmpty()) { displayMessage("error", "pleaseProvideSignature", "Please draw a signature."); return; }
    const signatureData = signatureRef.current.toDataURL("image/png");
    setLoading(true); setErrorMessage(null);
    try {
      const result = await updateProfile({ signature: signatureData });
      if (result) { setSignaturePreviewUrl(signatureData); displayMessage("success", "signatureSaved", "Signature saved!"); }
    } catch (err) { displayMessage("error", "failedToSaveSignature", err.response?.data?.message || "Failed to save signature."); }
    finally { setLoading(false); }
  };

  const clearSignaturePad = () => { if (signatureRef.current) signatureRef.current.clear(); };
  const undoSignature = () => { if (signatureRef.current) { const data = signatureRef.current.toData(); if (data.length) { data.pop(); signatureRef.current.fromData(data); } } };

  const handleProfileUpdate = async (e) => {
    e.preventDefault(); setLoading(true); setErrorMessage(null);
    try {
      const result = await updateProfile(formData); // formData already includes name, email, etc.
      if (result) displayMessage("success", "profileUpdated", "Profile updated!");
    } catch (err) { displayMessage("error", "failedToUpdateProfile", err.response?.data?.message || "Failed to update profile."); }
    finally { setLoading(false); }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) { displayMessage("error", "passwordsDoNotMatch", "New passwords do not match."); return; }
    if (passwordData.newPassword.length < 6) { displayMessage("error", "passwordMinLength", "Password must be at least 6 characters."); return; }
    setLoading(true); setErrorMessage(null);
    try {
      const result = await updateProfile({ currentPassword: passwordData.currentPassword, newPassword: passwordData.newPassword });
      if (result) { displayMessage("success", "passwordUpdated", "Password updated!"); setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" }); }
    } catch (err) { displayMessage("error", "failedToUpdatePassword", err.response?.data?.message || "Failed to update password."); }
    finally { setLoading(false); }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { displayMessage("error", "pleaseUploadImage", "Please upload an image file."); return; }
    if (file.size > 5 * 1024 * 1024) { displayMessage("error", "imageSizeTooLarge", "Image size should be less than 5MB."); return; }
    setImageLoading(true); setErrorMessage(null);
    try {
      const formPayload = new FormData(); formPayload.append("profileImage", file);
      const response = await authService.uploadProfileImage(formPayload); // Assuming this is the correct service call
      await updateProfile({ profileImage: response.data.profileImage }); // Update context
      displayMessage("success", "profileImageUpdated", "Profile image updated!");
    } catch (err) { displayMessage("error", "failedToUploadImage", err.response?.data?.message || "Failed to upload image."); }
    finally { setImageLoading(false); }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith("http")) return imagePath;
    const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
    return `${baseUrl}${imagePath.startsWith("/") ? "" : "/"}${imagePath}`;
  };


  return (
    <PageContainer title={t("userProfile")}>
      <Card elevation={0} sx={{ borderRadius: theme.shape.borderRadius * 1.5, mb: 3, overflow: 'visible' /* For avatar overlap */ }}>
        <Box sx={{ height: 160, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.8)} 0%, ${alpha(theme.palette.primary.dark, 0.9)} 100%)` }} />
        <Box sx={{ px: { xs: 2, md: 3 }, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'center', md: 'flex-end' }, mt: -10 }}>
          <Box sx={{ position: 'relative', mb: { xs: 1, md: 0 } }}>
            <Avatar
              src={getImageUrl(currentUser?.profileImage)}
              alt={currentUser?.name}
              sx={{ width: 140, height: 140, border: `4px solid ${theme.palette.background.paper}`, bgcolor: 'primary.main', fontSize: '3rem', boxShadow: theme.shadows[3] }}
            >
              {currentUser?.name ? currentUser.name[0].toUpperCase() : <PersonOutlineOutlinedIcon sx={{ fontSize: '3.5rem' }} />}
            </Avatar>
            <Tooltip title={t("changeProfilePicture")}>
              <IconButton onClick={() => fileInputRef.current?.click()} disabled={imageLoading} sx={{ position: 'absolute', bottom: 8, right: 8, bgcolor: 'background.paper', '&:hover': { bgcolor: alpha(theme.palette.background.paper, 0.8) }, boxShadow: theme.shadows[2] }}>
                {imageLoading ? <CircularProgress size={22} /> : <CameraAltIcon fontSize="small" color="primary" />}
              </IconButton>
            </Tooltip>
            <input type="file" ref={fileInputRef} style={{ display: "none" }} accept="image/*" onChange={handleImageChange} />
          </Box>
          <Box sx={{ flexGrow: 1, ml: { md: 3 }, textAlign: { xs: 'center', md: 'left' }, mb: { xs: 2, md: 2 } }}>
            <Typography variant="h4" fontWeight="bold">{currentUser?.name || t("user")}</Typography>
            <Typography variant="body1" color="text.secondary">{currentUser?.email}</Typography>
            <Chip icon={<SecurityOutlinedIcon fontSize="small" />} label={t(currentUser?.role || "userRole")} size="small" color="primary" variant="outlined" sx={{ mt: 0.5 }} />
          </Box>
        </Box>
        <Tabs value={tabValue} onChange={handleTabChange} indicatorColor="primary" textColor="primary" variant={isMobile ? "fullWidth" : "standard"} sx={{ "& .MuiTab-root": { minWidth: 120, textTransform: 'none' } }}>
          <Tab label={t("profileDetails")} icon={<PersonOutlineOutlinedIcon />} iconPosition="start" id="tab-profile" />
          <Tab label={t("securitySettings")} icon={<SecurityOutlinedIcon />} iconPosition="start" id="tab-security" />
          <Tab label={t("manageSignature")} icon={<GestureOutlinedIcon />} iconPosition="start" id="tab-signature" />
        </Tabs>
      </Card>

      {(errorMessage || authErrorHook) && <Alert severity="error" variant="filled" sx={{ mb: 2.5 }} onClose={() => setErrorMessage(null)}>{errorMessage || authErrorHook}</Alert>}
      {successMessage && <Alert severity="success" variant="filled" sx={{ mb: 2.5 }} onClose={() => setSuccessMessage(null)}>{successMessage}</Alert>}

      <Paper elevation={0} variant="outlined" sx={{ borderRadius: theme.shape.borderRadius * 1.5 }}>
        <TabPanel value={tabValue} index={0}>
          <Box component="form" onSubmit={handleProfileUpdate}>
            <Typography variant="h6" gutterBottom>{t("editProfileInformation")}</Typography>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}><TextField fullWidth label={t("fullName")} name="name" value={formData.name} onChange={handleInputChange} required InputProps={{ startAdornment: (<InputAdornment position="start"><PersonOutlineOutlinedIcon color="action" /></InputAdornment>) }} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label={t("email")} name="email" type="email" value={formData.email} disabled InputProps={{ startAdornment: (<InputAdornment position="start"><EmailOutlinedIcon color="action" /></InputAdornment>) }} helperText={t("emailCannotBeChanged")} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label={t("phoneNumber")} name="phoneNumber" value={formData.phoneNumber} onChange={handleInputChange} InputProps={{ startAdornment: (<InputAdornment position="start"><PhoneOutlinedIcon color="action" /></InputAdornment>) }} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label={t("department")} name="department" value={formData.department} onChange={handleInputChange} InputProps={{ startAdornment: (<InputAdornment position="start"><WorkOutlineOutlinedIcon color="action" /></InputAdornment>) }} /></Grid>
              <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                <Button type="submit" variant="contained" disabled={loading} startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveOutlinedIcon />}>{t("saveProfileChanges")}</Button>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box component="form" onSubmit={handlePasswordUpdate}>
            <Typography variant="h6" gutterBottom>{t("changePassword")}</Typography>
            <Grid container spacing={2.5}>
              <Grid item xs={12}><TextField fullWidth label={t("currentPassword")} name="currentPassword" type="password" value={passwordData.currentPassword} onChange={handlePasswordChange} required InputProps={{ startAdornment: (<InputAdornment position="start"><LockOpenOutlinedIcon color="action" /></InputAdornment>) }} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label={t("newPassword")} name="newPassword" type="password" value={passwordData.newPassword} onChange={handlePasswordChange} required InputProps={{ startAdornment: (<InputAdornment position="start"><PasswordOutlinedIcon color="action" /></InputAdornment>) }} helperText={t("passwordMinLengthInfo", { length: 6 })} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label={t("confirmNewPassword")} name="confirmPassword" type="password" value={passwordData.confirmPassword} onChange={handlePasswordChange} required error={passwordData.newPassword !== passwordData.confirmPassword && passwordData.confirmPassword.length > 0} helperText={passwordData.newPassword !== passwordData.confirmPassword && passwordData.confirmPassword.length > 0 ? t("passwordsDoNotMatch") : ""} InputProps={{ startAdornment: (<InputAdornment position="start"><PasswordOutlinedIcon color="action" /></InputAdornment>) }} /></Grid>
              <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                <Button type="submit" variant="contained" disabled={loading} startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveOutlinedIcon />}>{t("updatePassword")}</Button>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>{t("manageYourSignature")}</Typography>
          <Typography variant="body2" color="text.secondary" paragraph>{t("drawOrClearSignatureInfo")}</Typography>
          <Grid container spacing={2.5} alignItems="flex-start">
            <Grid item xs={12} md={7} ref={signatureContainerRef}>
              <Paper variant="outlined" sx={{ height: 230, position: 'relative', bgcolor: theme.palette.common.white, cursor: 'crosshair' }}>
                <SignatureCanvas ref={signatureRef} penColor={penColor} backgroundColor={alpha(theme.palette.common.white, 1)} canvasProps={{ style: { width: '100%', height: '100%' } }} />
              </Paper>
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} justifyContent="flex-start">
                <Button variant="outlined" onClick={clearSignaturePad} startIcon={<DeleteOutlineOutlinedIcon />} disabled={loading}>{t("clearPad")}</Button>
                <Button variant="outlined" onClick={undoSignature} startIcon={<UndoOutlinedIcon />} disabled={loading}>{t("undo")}</Button>
                <Button variant="contained" onClick={saveSignature} startIcon={loading ? <CircularProgress size={20} /> : <SaveOutlinedIcon />} disabled={loading}>{t("saveThisSignature")}</Button>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
                <Typography variant="caption">{t("penColor")}:</Typography>
                <input type="color" value={penColor} onChange={(e) => setPenColor(e.target.value)} style={{ width: 30, height: 30, border: 'none', padding: 0, background: 'transparent', cursor: 'pointer' }} />
                <Typography variant="caption">{t("penSize")}: {penSize}</Typography>
                <input type="range" min="1" max="5" value={penSize} onChange={(e) => setPenSize(parseInt(e.target.value))} style={{ flexGrow: 1, maxWidth: 100 }} />
              </Stack>
            </Grid>
            {signaturePreviewUrl && (
              <Grid item xs={12} md={5}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>{t("currentSavedSignature")}</Typography>
                <Paper variant="outlined" sx={{ p: 1, textAlign: 'center', bgcolor: theme.palette.common.white }}>
                  <img src={signaturePreviewUrl} alt={t("yourSignature")} style={{ maxWidth: '100%', maxHeight: 120, border: `1px solid ${theme.palette.divider}` }} />
                </Paper>
              </Grid>
            )}
          </Grid>
        </TabPanel>
      </Paper>
    </PageContainer>
  );
};

export default Profile;
